import React from 'react';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import MetricCard, { BreakdownCard } from './MetricCard';
import { CARD_KEYS, CARDS, analyticsUiTokens as T } from '../../constants/userAnalytics';
import { cardValue, fmtCount } from '../../utils/analyticsMetrics';
import { formatEtDate } from '../../utils/formatEt';

// Module-level so a card's props stay referentially stable and React.memo can skip re-renders.
const CHIPS = {
  total: { bg: T.slate[100], color: T.navy, hoverBg: T.navy, hoverColor: '#ffffff' },
  dau: { bg: T.emerald[50], color: T.emerald[600], hoverBg: T.emerald[600], hoverColor: '#ffffff' },
  wau: { bg: T.sky[50], color: T.sky[600], hoverBg: T.sky[600], hoverColor: '#ffffff' },
  mau: { bg: T.indigo[50], color: T.navy, hoverBg: T.navy, hoverColor: '#ffffff' },
};
const MAU_BADGE = { text: 'Active Pool', bg: T.emerald[50], color: T.emerald[700] };

const OverviewSection = ({ summary, showBreakdown, onToggleBreakdown, onOpenCard }) => {
  const { data, status } = summary;
  const hasData = Boolean(data);

  const asOfWord = useLocalText('As of');
  const sevenDays = useLocalText('7 days to');
  const thirtyDays = useLocalText('30 days to');
  const distinct = useLocalText('Distinct people');
  const previousLabel = useLocalText('Previous period');

  const active = data?.active;
  const asOf = active?.asOf ? formatEtDate(active.asOf) : '';
  const text = (key) => (hasData ? fmtCount(cardValue(data, key)) : '—');
  const previous = (point) => (typeof point?.previous === 'number' ? `${previousLabel}: ${fmtCount(point.previous)}` : undefined);
  const immersiveOnly = data?.capabilities?.cmsActivityTracked === false;
  const both = data?.totals?.both ?? 0;

  const shared = { status, hasData, onOpen: onOpenCard };

  return (
    <Box component="section" aria-labelledby="ua-overview-title" sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { lg: 'center' }, justifyContent: 'space-between', gap: 1.5 }}>
        <Box>
          <Typography id="ua-overview-title" component="h2" sx={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: T.slate[800] }}>
            <LocalText>Users & Growth Overview</LocalText>
          </Typography>
          <Typography sx={{ fontSize: 12, color: T.slate[500] }}>
            <LocalText>Total users and active user counts (DAU, WAU, MAU)</LocalText>
          </Typography>
          {immersiveOnly && (
            <Typography sx={{ fontSize: 11, color: T.slate[500], mt: 0.25 }}>
              <LocalText>Active users count Immersive sessions only — CMS logins aren't recorded.</LocalText>
            </Typography>
          )}
        </Box>

        <FormControlLabel
          sx={{
            alignSelf: { xs: 'flex-start', lg: 'auto' }, m: 0, pl: 0.5, pr: 1.5, py: 0.25, gap: 0.5,
            bgcolor: '#ffffff', border: `1px solid ${T.cardBorder}`, borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(15,23,42,0.05)', '&:hover': { bgcolor: T.slate[50] },
            '& .MuiFormControlLabel-label': { fontSize: 12, fontWeight: 600, color: T.slate[700] },
          }}
          control={(
            <Switch
              size="small"
              checked={showBreakdown}
              onChange={onToggleBreakdown}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#ffffff' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: T.navy, opacity: 1 },
              }}
            />
          )}
          label={<LocalText>Show CMS & Immersive Users</LocalText>}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: { xs: 1.75, sm: 2 },
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <MetricCard
            {...shared}
            cardKey={CARD_KEYS.TOTAL}
            label={CARDS.total.label}
            Icon={CARDS.total.Icon}
            chip={CHIPS.total}
            valueText={text('total')}
            valueColor={T.navy}
            hoverBorder="rgba(13,43,87,0.4)"
            caption={hasData ? distinct : ''}
            breakdownOpen={showBreakdown}
            onToggleBreakdown={onToggleBreakdown}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <MetricCard
            {...shared}
            cardKey={CARD_KEYS.DAU}
            label={CARDS.dau.label}
            Icon={CARDS.dau.Icon}
            chip={CHIPS.dau}
            valueText={text('dau')}
            valueColor={T.navy}
            hoverBorder="rgba(16,185,129,0.5)"
            hoverLink={T.emerald[700]}
            caption={asOf ? `${asOfWord} ${asOf}` : ''}
            deltaKind="emerald"
            deltaPct={active?.dau?.deltaPct}
            deltaHint={previous(active?.dau)}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <MetricCard
            {...shared}
            cardKey={CARD_KEYS.WAU}
            label={CARDS.wau.label}
            Icon={CARDS.wau.Icon}
            chip={CHIPS.wau}
            valueText={text('wau')}
            valueColor={T.teal}
            hoverBorder="rgba(14,165,233,0.5)"
            hoverLink={T.sky[700]}
            caption={asOf ? `${sevenDays} ${asOf}` : ''}
            deltaKind="sky"
            deltaPct={active?.wau?.deltaPct}
            deltaHint={previous(active?.wau)}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <MetricCard
            {...shared}
            cardKey={CARD_KEYS.MAU}
            label={CARDS.mau.label}
            Icon={CARDS.mau.Icon}
            chip={CHIPS.mau}
            valueText={text('mau')}
            valueColor={T.green}
            hoverBorder="rgba(5,150,105,0.5)"
            hoverLink={T.emerald[700]}
            caption={asOf ? `${thirtyDays} ${asOf}` : ''}
            badge={MAU_BADGE}
          />
        </Box>
      </Box>

      {showBreakdown && (
        <Box sx={{ pt: 0.5 }}>
          <Box sx={{ bgcolor: 'rgba(241,245,249,0.8)', border: `1px solid ${T.cardBorder}`, borderRadius: '12px', p: { xs: 1.75, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: T.navy }} />
                <Typography component="h3" sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: T.navy }}>
                  <LocalText>Optional Product Breakdown (Total Users by Platform)</LocalText>
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: T.slate[500] }}>
                <LocalText>Click any card below to inspect user login details</LocalText>
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: { xs: 1.75, sm: 2 } }}>
              <BreakdownCard
                {...shared}
                cardKey={CARD_KEYS.IMMERSIVE}
                label={CARDS.immersive.label}
                tag="3D / VR Spaces"
                accent={T.orange}
                tagBg={T.orangeSoft[50]}
                tagBorder={T.orangeSoft[200]}
                subtitle="Users who logged into Immersive 3D spaces"
                hint="Click to view user directory"
                Icon={ViewInArOutlinedIcon}
                iconBg={T.orangeSoft[50]}
                valueText={text('immersive')}
              />
              <BreakdownCard
                {...shared}
                cardKey={CARD_KEYS.CMS}
                label={CARDS.cms.label}
                tag="CMS Portal"
                accent={T.teal}
                tagBg={T.sky[50]}
                tagBorder={T.sky[200]}
                subtitle="CMS portal members who have signed in"
                hint="Click to view CMS members"
                Icon={TuneOutlinedIcon}
                iconBg={T.sky[50]}
                valueText={text('cms')}
              />
            </Box>

            {both > 0 && (
              <Typography data-testid="both-footnote" sx={{ fontSize: 11, color: T.slate[600] }}>
                <strong>{fmtCount(both)}</strong>{' '}
                <LocalText>users have both CMS and Immersive accounts, so these two cards can add up to more than Total Users.</LocalText>
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OverviewSection;
